/**
 * 社員の分布：隣り合う2象限への寄りの文言（v3で新規に書き起こしたもの）。
 *
 * DB04 は「1象限への集中」「対角の両極」「中央集中」「均等」しか区分を持たず、
 * 隣り合う2象限に寄っている組織（CD＝右／AB＝左／AD＝上／BC＝下）の文言が無い。
 * そのため CD寄りの組織に「持ち味が四方に散らばっている」と出る不具合があった。
 *
 * ここでは DB04 と同じ体裁（見出し1行＋本文2文）と文言ルールで4件を用意する。
 *   - 断定を避け「〜しやすい」「〜傾向があります」で止める
 *   - 原因を個人に置かない。主語は組織
 *   - 位置に優劣をつけない（強みを述べてから、手薄になりやすい面を添える）
 *   - 4軸の値そのものを改善目標にしない（09-RULE-改善禁止）
 *   - 1文50字以内を目安
 *
 * 【ダイヤモンドの配置】A 論理＝左上／D 創造＝右上／C 共感＝右下／B 着実＝左下。
 *  隣り合う2象限は「右（C・D）」「左（A・B）」「上（A・D）」「下（B・C）」の4通り。
 *  寄っている側の反対側が手薄になる、という構造で書いている。
 *
 * DB化するときは DB04 に 04-隣接CD などとして移す。
 */
import type { AxisKey } from './gb74Report';

export interface DistLeaningText {
  id: string;
  見出し: string;
  本文: string;
}

/** 隣接ペアのキー（軸をA→B→C→Dの順に並べたもの）。 */
export type LeaningKey = 'CD' | 'AB' | 'AD' | 'BC';

export const DIST_LEANING_TEXT: Record<LeaningKey, DistLeaningText> = {
  CD: {
    id: '04-隣接CD',
    見出し: '人と変化のほうに寄っている組織',
    本文: '人を気にかける力と、新しいことに動く力の両方がある状態です。一方で、手順を整えることや、決めたことを検証する動きが後回しになりやすい傾向があります。',
  },
  AB: {
    id: '04-隣接AB',
    見出し: '手順と安定のほうに寄っている組織',
    本文: '筋道立てて考える力と、決めたことを守り切る力の両方がある状態です。一方で、人の気持ちを汲む場面や、新しい試みを起こす場面が手薄になりやすい傾向があります。',
  },
  AD: {
    id: '04-隣接AD',
    見出し: '考えることと動くことに寄っている組織',
    本文: '筋道を立てる力と、新しいことに踏み出す力の両方がある状態です。一方で、決めたことを定着させる動きや、周囲への目配りが薄くなりやすい傾向があります。',
  },
  BC: {
    id: '04-隣接BC',
    見出し: '支え合って続けるほうに寄っている組織',
    本文: '決めたことを守る力と、互いを支え合う力の両方がある状態です。一方で、前提を問い直す議論や、新しい挑戦が起きにくくなりやすい傾向があります。',
  },
};

/**
 * 優勢軸が3つある組織の文言（DB04に該当区分が無いため新規に用意）。
 *
 * 【なぜ必要か】散布図の座標は相対位置（横 =(C+D)−(A+B) / 縦 =(A+D)−(B+C)）なので、
 * 1軸だけが低い組織は、必ずその反対側の象限に全員が集まる。
 * 例：A・C・D が高く B が低い組織は、全員が右上（D）に入り、
 * 「変化と推進に強みが集まっている」と表示されてしまう。実態は3方向に広い。
 * 位置だけを見る限りこれは避けられないため、優勢軸の数を判定に加え、
 * 3軸のときは象限の集中として語らない。
 *
 * キーは「薄い軸」。3軸が優勢なら、残る1軸が必ず薄い。
 */
export const DIST_THREE_AXIS_TEXT: Record<AxisKey, DistLeaningText> = {
  D: {
    id: '04-3軸-D欠',
    見出し: '3つの持ち味が広くあり、変化を起こす動きだけが薄い組織',
    本文: '筋道を立てる力・決めたことを守る力・人への目配りの3つが広く見られる状態です。一方で、新しいことを始める動きが起きにくく、現状の延長で進みやすい傾向があります。',
  },
  A: {
    id: '04-3軸-A欠',
    見出し: '3つの持ち味が広くあり、筋道を立てる動きだけが薄い組織',
    本文: '決めたことを守る力・人への目配り・新しさへ動く力の3つが広く見られる状態です。一方で、前提を整理して筋道を立てる場面が手薄になりやすい傾向があります。',
  },
  B: {
    id: '04-3軸-B欠',
    見出し: '3つの持ち味が広くあり、決めたことを守る動きだけが薄い組織',
    本文: '筋道を立てる力・人への目配り・新しさへ動く力の3つが広く見られる状態です。一方で、決めたことを続ける動きが弱く、取り組みが定着しにくい傾向があります。',
  },
  C: {
    id: '04-3軸-C欠',
    見出し: '3つの持ち味が広くあり、人への目配りだけが薄い組織',
    本文: '筋道を立てる力・決めたことを守る力・新しさへ動く力の3つが広く見られる状態です。一方で、人の気持ちを汲む場面が後回しになりやすい傾向があります。',
  },
};

/**
 * 3象限にだけ人がいる場合の文言（1つの象限が空）。
 *
 * 4象限すべてに人がいないと「四方に散らばっている（分散型）」とは言えないが、
 * 「際立った偏りが見られない（中間型）」だけでは、1方向がまるごと空いている
 * という明確な特徴を伝えられない。そのため専用の文言を用意する。
 *
 * 【DIST_THREE_AXIS_TEXT との違い】
 *  あちらは優勢率（その軸が7点以上の社員の割合）が3軸ぶん高い場合で、
 *  「持ち味として薄い」という話。こちらは散布図の位置の話で、
 *  「その向きに立っている人がいない」という話。別のことを言っている。
 *
 * キーは「空いている象限」。
 */
export const DIST_THREE_QUADRANT_TEXT: Record<AxisKey, DistLeaningText> = {
  D: {
    id: '04-3象限-D欠',
    見出し: '3方向に広がり、変化を起こす向きだけが空いている組織',
    本文: '筋道・守り・人への目配りの3方向に人が広がっている状態です。一方で、新しいことに踏み出す位置に立つ方がおらず、変える話そのものが起こりにくい傾向があります。',
  },
  A: {
    id: '04-3象限-A欠',
    見出し: '3方向に広がり、筋道を立てる向きだけが空いている組織',
    本文: '守り・人への目配り・新しさへの動きの3方向に人が広がっている状態です。一方で、前提を整理して筋道を立てる位置に立つ方がおらず、議論が発散したまま終わりやすい傾向があります。',
  },
  B: {
    id: '04-3象限-B欠',
    見出し: '3方向に広がり、守りの向きだけが空いている組織',
    本文: '筋道・人への目配り・新しさへの動きの3方向に人が広がっている状態です。一方で、決めたことを守り続ける位置に立つ方がおらず、取り組みが定着しにくい傾向があります。',
  },
  C: {
    id: '04-3象限-C欠',
    見出し: '3方向に広がり、人への目配りの向きだけが空いている組織',
    本文: '筋道・守り・新しさへの動きの3方向に人が広がっている状態です。一方で、人の気持ちに寄る位置に立つ方がおらず、決めたことが個人の事情を置き去りにしやすい傾向があります。',
  },
};

/**
 * 中間型（どの条件にも当てはまらない）の文言。
 *
 * DB04 には「均等（四方に散らばっている）」しか受け皿が無かったため、
 * 中間型にも 04-均等 を当てていた。その結果、型が「中間型（特定の偏りは
 * 見られません）」なのに分布が「持ち味が四方に散らばっている」と出て、
 * 別のことを言っている状態になっていた。中間型には中間型の文言を用意する。
 */
export const DIST_NEUTRAL_TEXT: DistLeaningText = {
  id: '04-中間',
  見出し: '際立った偏りが見られない組織',
  本文: '持ち味の分かれ方に、はっきりした型が出ていない状態です。特定の観点に寄っているわけでも、四方に均等に散らばっているわけでもないため、人の組み合わせより、仕事の進め方から手がかりを探すほうが見つけやすい傾向があります。',
};

/** 隣接ペア（順不同の2軸）からキーを作る。 */
export function leaningKeyOf(pair: [AxisKey, AxisKey]): LeaningKey {
  const order: AxisKey[] = ['A', 'B', 'C', 'D'];
  return [...pair].sort((a, b) => order.indexOf(a) - order.indexOf(b)).join('') as LeaningKey;
}
